import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Chip,
  Button,
  IconButton,
  TextField,
  InputAdornment,
  MenuItem,
  Alert,
  CircularProgress,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Divider,
  Card,
  CardContent,
  Snackbar,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  Search,
  Visibility,
  CheckCircle,
  Cancel,
  Refresh,
  WhatsApp,
  Email,
  ContentCopy,
  VerifiedUser,
  Link,
} from '@mui/icons-material';
import { 
  backofficeManagementService, 
  SignupRequest, 
  SignupRequestListResponse,
  ApprovalRequest 
} from '../../services/backofficeManagementService';

const MOWILEX_COLORS = {
  primary: '#E31E24',
  primaryDark: '#B71C1C',
  primaryLight: '#FF5252',
  white: '#FFFFFF',
  lightGray: '#F5F5F5',
  darkGray: '#757575',
  success: '#4CAF50',
  warning: '#FF9800',
  error: '#F44336',
};

const SignupRequestsPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [requests, setRequests] = useState<SignupRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  
  // Action dialogs
  const [approveDialog, setApproveDialog] = useState<{open: boolean, request: SignupRequest | null}>({
    open: false,
    request: null
  });
  const [rejectDialog, setRejectDialog] = useState<{open: boolean, request: SignupRequest | null}>({
    open: false,
    request: null
  });
  const [viewDialog, setViewDialog] = useState<{open: boolean, request: SignupRequest | null}>({
    open: false,
    request: null
  });
  const [epicorDialog, setEpicorDialog] = useState<{open: boolean, request: SignupRequest | null, result: any}>({
    open: false,
    request: null,
    result: null
  });
  const [magicLinkDialog, setMagicLinkDialog] = useState<{open: boolean, request: SignupRequest | null}>({
    open: false,
    request: null
  });
  const [editDialog, setEditDialog] = useState<{open: boolean, request: SignupRequest | null}>({
    open: false,
    request: null
  });
  const [verifyConfirmDialog, setVerifyConfirmDialog] = useState<{open: boolean, request: SignupRequest | null}>({
    open: false,
    request: null
  });

  // Form data
  const [approvalForm, setApprovalForm] = useState<ApprovalRequest>({
    admin_notes: '',
    send_magic_link: true,
    delivery_method: 'email',
    custom_message: ''
  });
  const [magicLinkResult, setMagicLinkResult] = useState<{open: boolean, url: string, email: string}>({open: false, url: '', email: ''});
  const [rejectionReason, setRejectionReason] = useState('');
  const [magicLinkForm, setMagicLinkForm] = useState({
    delivery_method: 'email' as 'email' | 'whatsapp' | 'manual',
    regenerate_token: false,
    custom_message: ''
  });
  const [editForm, setEditForm] = useState({
    full_name: '',
    phone: '',
    company_name: '',
    contact_person: '',
    mowilex_customer_id: '',
    territory: '',
    address1: '',
    address2: '',
    city: '',
    province: '',
    kecamatan: '',
    kelurahan: '',
    postal_code: '',
    epicor_customer_id: ''
  });

  // Loading states
  const [epicorLoading, setEpicorLoading] = useState<string | null>(null);
  const [approveLoading, setApproveLoading] = useState(false);
  const [rejectLoading, setRejectLoading] = useState(false);
  const [magicLinkLoading, setMagicLinkLoading] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [i18nReady, setI18nReady] = useState(i18n.isInitialized);

  useEffect(() => {
    loadRequests();
  }, [page, rowsPerPage, search, statusFilter]);

  useEffect(() => {
    const handleLanguageChanged = () => {
      setI18nReady(i18n.isInitialized);
    };
    
    i18n.on('initialized', handleLanguageChanged);
    i18n.on('languageChanged', handleLanguageChanged);
    
    return () => {
      i18n.off('initialized', handleLanguageChanged);
      i18n.off('languageChanged', handleLanguageChanged);
    };
  }, []);

  useEffect(() => {
    console.log('Current language:', i18n.language, 'Ready:', i18n.isInitialized);
    console.log('Available languages:', i18n.languages);
  }, [i18n.language, i18n.isInitialized]);

  const loadRequests = async () => {
    try {
      setLoading(true);
      setError('');
      
      const response: SignupRequestListResponse = await backofficeManagementService.getSignupRequests(
        page + 1, // API uses 1-based pagination
        rowsPerPage,
        statusFilter || undefined,
        search || undefined
      );
      
      setRequests(response?.items || []);
      setTotal(response?.total || 0);
    } catch (error: any) {
      setError('Failed to load signup requests: ' + error.message);
      setRequests([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (value: string) => {
    setSearch(value);
    setPage(0);
  };

  const handleStatusFilter = (status: string) => {
    setStatusFilter(status);
    setPage(0);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved':
        return 'success';
      case 'rejected':
        return 'error';
      case 'under_review':
        return 'warning';
      case 'pending':
      default:
        return 'default';
    }
  };

  const getStatusLabel = (status: string) => {
    // Wait for i18n to be ready
    if (!i18nReady) {
      return status;
    }
    
    switch (status) {
      case 'pending':
        return t('backoffice.signupRequests.status.pending');
      case 'under_review':
        return t('backoffice.signupRequests.status.underReview');
      case 'approved':
        return t('backoffice.signupRequests.status.approved');
      case 'rejected':
        return t('backoffice.signupRequests.status.rejected');
      default:
        return status;
    }
  };

  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return '-';
    try {
      // Use current language for date formatting
      const locale = t('locale') === 'id' ? 'id-ID' : 'en-US';
      return new Date(dateString).toLocaleDateString(locale, {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  const handleVerifyEpicor = async (request: SignupRequest) => {
    try {
      setVerifyConfirmDialog({ open: false, request: null });
      setEpicorLoading(request.id);
      const result = await backofficeManagementService.verifySignupRequestWithEpicor(request.id);
      setEpicorDialog({ open: true, request, result });
      setSuccess(t('backoffice.signupRequests.messages.verifySuccess'));
      // Reload requests to update the UI with verification status
      loadRequests();
    } catch (error: any) {
      setError(t('backoffice.signupRequests.messages.error') + ': ' + error.message);
    } finally {
      setEpicorLoading(null);
    }
  };

  const handleApprove = async () => {
    if (!approveDialog.request) return;
    
    try {
      setApproveLoading(true);
      const response = await backofficeManagementService.approveSignupRequest(approveDialog.request.id, approvalForm);
      
      // Show magic link URL if delivery method is manual
      if (approvalForm.send_magic_link && approvalForm.delivery_method === 'manual' && response.magic_link_url) {
        setMagicLinkResult({
          open: true,
          url: response.magic_link_url,
          email: approveDialog.request.email
        });
      }
      
      setSuccess(t('backoffice.signupRequests.messages.approveSuccess'));
      setApproveDialog({ open: false, request: null });
      setApprovalForm({
        admin_notes: '',
        send_magic_link: true,
        delivery_method: 'email',
        custom_message: ''
      });
      loadRequests();
    } catch (error: any) {
      setError('Gagal menyetujui permintaan: ' + error.message);
    } finally {
      setApproveLoading(false);
    }
  };

  const handleReject = async () => {
    if (!rejectDialog.request || !rejectionReason.trim()) return;
    
    try {
      setRejectLoading(true);
      await backofficeManagementService.rejectSignupRequest(rejectDialog.request.id, rejectionReason);
      setSuccess(t('backoffice.signupRequests.messages.rejectSuccess'));
      setRejectDialog({ open: false, request: null });
      setRejectionReason('');
      loadRequests();
    } catch (error: any) {
      setError('Gagal menolak permintaan: ' + error.message);
    } finally {
      setRejectLoading(false);
    }
  };

  const handleSendMagicLink = async () => {
    if (!magicLinkDialog.request) return;
    
    try {
      setMagicLinkLoading(true);
      const response = await backofficeManagementService.sendMagicLinkForSignupRequest(
        magicLinkDialog.request.id, 
        magicLinkForm
      );
      
      // Show magic link URL if delivery method is manual
      if (magicLinkForm.delivery_method === 'manual' && response.magic_link_url) {
        setMagicLinkResult({
          open: true,
          url: response.magic_link_url,
          email: magicLinkDialog.request.email
        });
      }
      
      setSuccess(t('backoffice.signupRequests.messages.magicLinkSuccess'));
      setMagicLinkDialog({ open: false, request: null });
      setMagicLinkForm({
        delivery_method: 'email',
        regenerate_token: false,
        custom_message: ''
      });
    } catch (error: any) {
      setError('Gagal mengirim magic link: ' + error.message);
    } finally {
      setMagicLinkLoading(false);
    }
  };

  const handleEdit = async () => {
    if (!editDialog.request) return;
    
    try {
      setEditLoading(true);
      await backofficeManagementService.updateSignupRequest(editDialog.request.id, editForm);
      setSuccess(t('backoffice.signupRequests.messages.editSuccess'));
      setEditDialog({ open: false, request: null });
      loadRequests();
    } catch (error: any) {
      setError('Gagal memperbarui data: ' + error.message);
    } finally {
      setEditLoading(false);
    }
  };

  const openEditDialog = (request: SignupRequest) => {
    setEditForm({
      full_name: request.full_name || '',
      phone: request.phone || '',
      company_name: request.company_name || '',
      contact_person: request.contact_person || '',
      mowilex_customer_id: request.mowilex_customer_id || '',
      territory: request.territory || '',
      address1: request.address1 || '',
      address2: request.address2 || '',
      city: request.city || '',
      province: request.province || '',
      kecamatan: request.kecamatan || '',
      kelurahan: request.kelurahan || '',
      postal_code: request.postal_code || '',
      epicor_customer_id: request.epicor_customer_id || ''
    });
    setEditDialog({ open: true, request });
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header with Mowilex Logo */}
      <Paper 
        elevation={0}
        sx={{ 
          p: 4, 
          mb: 3, 
          backgroundColor: '#FAFAFA',
          borderLeft: `4px solid ${MOWILEX_COLORS.primary}`,
          borderRadius: 2,
          border: '1px solid #E0E0E0'
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography 
              variant="h4" 
              component="h1" 
              sx={{ 
                color: '#1A1A1A', 
                fontWeight: 600,
                fontSize: '1.75rem'
              }}
            >
              {t('backoffice.signupRequests.title')}
            </Typography>
            <Typography 
              variant="subtitle1" 
              sx={{ 
                color: '#666666',
                fontSize: '0.95rem',
                mt: 0.5
              }}
            >
              {t('backoffice.signupRequests.subtitle')}
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<Refresh />}
            onClick={loadRequests}
            disabled={loading}
            sx={{
              backgroundColor: MOWILEX_COLORS.primary,
              color: MOWILEX_COLORS.white,
              boxShadow: 'none',
              px: 3,
              py: 1,
              textTransform: 'none',
              fontWeight: 500,
              '&:hover': {
                backgroundColor: MOWILEX_COLORS.primaryDark,
                boxShadow: '0 2px 8px rgba(227, 30, 36, 0.3)',
              },
              '&:disabled': {
                backgroundColor: '#E0E0E0',
                color: '#999999',
              }
            }}
          >
            {t('common.refresh')}
          </Button>
        </Box>
      </Paper>

      {/* Alerts */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError('')}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="error" onClose={() => setError('')}>
          {error}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!success}
        autoHideDuration={4000}
        onClose={() => setSuccess('')}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="success" onClose={() => setSuccess('')}>
          {success}
        </Alert>
      </Snackbar>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box display="flex" gap={2} alignItems="center">
          <TextField
            placeholder={t('backoffice.signupRequests.search')}
            size="small"
            value={search}
            onChange={(e) => handleSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 300 }}
          />
          
          <TextField
            select
            label="Status"
            size="small"
            value={statusFilter}
            onChange={(e) => handleStatusFilter(e.target.value)}
            sx={{ minWidth: 150 }}
          >
            <MenuItem value="">{t('backoffice.signupRequests.filters.allStatuses')}</MenuItem>
            <MenuItem value="pending">{t('backoffice.signupRequests.filters.pending')}</MenuItem>
            <MenuItem value="under_review">{t('backoffice.signupRequests.filters.underReview')}</MenuItem>
            <MenuItem value="approved">{t('backoffice.signupRequests.filters.approved')}</MenuItem>
            <MenuItem value="rejected">{t('backoffice.signupRequests.filters.rejected')}</MenuItem>
          </TextField>
        </Box>
      </Paper>

      {/* Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t('backoffice.signupRequests.table.company')}</TableCell>
              <TableCell>{t('backoffice.signupRequests.fields.contactPerson')}</TableCell>
              <TableCell>{t('backoffice.signupRequests.table.email')}</TableCell>
              <TableCell>{t('backoffice.signupRequests.table.territory')}</TableCell>
              <TableCell>{t('backoffice.signupRequests.table.status')}</TableCell>
              <TableCell>{t('backoffice.signupRequests.table.submittedAt')}</TableCell>
              <TableCell>{t('backoffice.signupRequests.table.actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : !requests || requests.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography color="textSecondary">{t('backoffice.signupRequests.noRequestsFound')}</Typography>
                </TableCell>
              </TableRow>
            ) : (
              requests.map((request) => (
                <TableRow key={request.id} hover>
                  <TableCell>
                    <Typography fontWeight="bold">{request.company_name}</Typography>
                    {request.mowilex_customer_id && (
                      <Typography variant="caption" color="textSecondary">
                        ID: {request.mowilex_customer_id}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography>{request.contact_person}</Typography>
                    <Typography variant="caption" color="textSecondary">
                      {request.phone}
                    </Typography>
                  </TableCell>
                  <TableCell>{request.email}</TableCell>
                  <TableCell>{request.territory}</TableCell>
                  <TableCell>
                    <Chip
                      label={getStatusLabel(request.status)}
                      color={getStatusColor(request.status)}
                      size="small"
                    />
                    {request.epicor_sync_status === 'synced' && (
                      <Chip
                        label="Epicor ✓"
                        color="success"
                        size="small"
                        sx={{ ml: 1 }}
                      />
                    )}
                    {request.status === 'pending' && request.epicor_sync_status !== 'synced' && (
                      <Chip
                        label="Verification Required"
                        color="warning"
                        size="small"
                        sx={{ ml: 1 }}
                      />
                    )}
                  </TableCell>
                  <TableCell>{formatDate(request.submitted_at)}</TableCell>
                  <TableCell>
                    <Box display="flex" gap={1} flexWrap="wrap">
                      <Tooltip title={t('backoffice.signupRequests.actions.view')}>
                        <IconButton
                          size="small"
                          onClick={() => {
                            // Debug: Log the request data when viewing
                            console.log('=== DEBUG: Viewing signup request ===');
                            console.log('Request data:', {
                              email: request.email,
                              company_name: request.company_name,
                              address1: request.address1,
                              address2: request.address2,
                              city: request.city,
                              province: request.province,
                              kecamatan: request.kecamatan,
                              kelurahan: request.kelurahan,
                              postal_code: request.postal_code
                            });
                            console.log('======================================');
                            setViewDialog({ open: true, request });
                          }}
                        >
                          <Visibility />
                        </IconButton>
                      </Tooltip>
                      
                      <Tooltip title={t('backoffice.signupRequests.actions.verify')}>
                        <IconButton
                          size="small"
                          color="info"
                          onClick={() => setVerifyConfirmDialog({ open: true, request })}
                          disabled={epicorLoading === request.id}
                        >
                          {epicorLoading === request.id ? <CircularProgress size={16} /> : <VerifiedUser />}
                        </IconButton>
                      </Tooltip>
                      
                      {request.status === 'pending' && (
                        <>
                          <Tooltip 
                            title={
                              request.epicor_sync_status === 'synced' 
                                ? t('backoffice.signupRequests.actions.approve')
                                : 'Epicor verification required before approval'
                            }
                          >
                            <span>
                              <IconButton
                                size="small"
                                onClick={() => setApproveDialog({ open: true, request })}
                                disabled={request.epicor_sync_status !== 'synced'}
                                sx={{
                                  color: request.epicor_sync_status === 'synced' 
                                    ? MOWILEX_COLORS.success 
                                    : '#CCCCCC',
                                  '&:hover': {
                                    backgroundColor: request.epicor_sync_status === 'synced' 
                                      ? `${MOWILEX_COLORS.success}20` 
                                      : 'transparent'
                                  },
                                  '&:disabled': {
                                    color: '#CCCCCC'
                                  }
                                }}
                              >
                                <CheckCircle />
                              </IconButton>
                            </span>
                          </Tooltip>
                          
                          <Tooltip title={t('backoffice.signupRequests.actions.reject')}>
                            <IconButton
                              size="small"
                              onClick={() => setRejectDialog({ open: true, request })}
                              sx={{
                                color: MOWILEX_COLORS.error,
                                '&:hover': {
                                  backgroundColor: `${MOWILEX_COLORS.error}20`
                                }
                              }}
                            >
                              <Cancel />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                      
                      {request.status === 'approved' && (
                        <Tooltip title={t('backoffice.signupRequests.actions.sendMagicLink')}>
                          <IconButton
                            size="small"
                            onClick={() => setMagicLinkDialog({ open: true, request })}
                            sx={{
                              color: MOWILEX_COLORS.primary,
                              '&:hover': {
                                backgroundColor: `${MOWILEX_COLORS.primary}20`
                              }
                            }}
                          >
                            <Link />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[10, 20, 50]}
          component="div"
          count={total}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
        />
      </TableContainer>

      {/* View Dialog */}
      <Dialog open={viewDialog.open} onClose={() => setViewDialog({ open: false, request: null })} maxWidth="md" fullWidth>
        <DialogTitle>{t('backoffice.signupRequests.dialogs.viewTitle')}</DialogTitle>
        <DialogContent>
          {viewDialog.request && (
            <Box sx={{ mt: 2 }}>
              <Box sx={{ display: 'flex', gap: 3, mb: 3, flexWrap: 'wrap' }}>
                <Box sx={{ flex: '1 1 300px' }}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>{t('backoffice.signupRequests.dialogs.companyInfo')}</Typography>
                      <Typography><strong>{t('backoffice.signupRequests.fields.companyName')}:</strong> {viewDialog.request.company_name}</Typography>
                      <Typography><strong>{t('backoffice.signupRequests.fields.email')}:</strong> {viewDialog.request.email}</Typography>
                      <Typography><strong>{t('backoffice.signupRequests.fields.mowilexCustomerId')}:</strong> {viewDialog.request.mowilex_customer_id || t('backoffice.signupRequests.fields.none')}</Typography>
                      <Typography><strong>{t('backoffice.signupRequests.fields.territory')}:</strong> {viewDialog.request.territory}</Typography>
                    </CardContent>
                  </Card>
                </Box>
                
                <Box sx={{ flex: '1 1 300px' }}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>{t('backoffice.signupRequests.dialogs.contactInfo')}</Typography>
                      <Typography><strong>{t('backoffice.signupRequests.fields.contactPerson')}:</strong> {viewDialog.request.contact_person || '-'}</Typography>
                      <Typography><strong>{t('backoffice.signupRequests.fields.fullName')}:</strong> {viewDialog.request.full_name || '-'}</Typography>
                      <Typography><strong>{t('backoffice.signupRequests.fields.phone')}:</strong> {viewDialog.request.phone || '-'}</Typography>
                    </CardContent>
                  </Card>
                </Box>
              </Box>
              
              <Box sx={{ mb: 3 }}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>{t('backoffice.signupRequests.dialogs.address')}</Typography>
                    <Typography><strong>{t('backoffice.signupRequests.fields.address1')}:</strong> {viewDialog.request.address1 || '-'}</Typography>
                    {viewDialog.request.address2 && (
                      <Typography><strong>{t('backoffice.signupRequests.fields.address2')}:</strong> {viewDialog.request.address2}</Typography>
                    )}
                    <Typography><strong>{t('backoffice.signupRequests.fields.city')}:</strong> {viewDialog.request.city || '-'}</Typography>
                    <Typography><strong>{t('backoffice.signupRequests.fields.province')}:</strong> {viewDialog.request.province || '-'}</Typography>
                    <Typography><strong>{t('backoffice.signupRequests.fields.district')}:</strong> {viewDialog.request.kecamatan || '-'}</Typography>
                    <Typography><strong>{t('backoffice.signupRequests.fields.village')}:</strong> {viewDialog.request.kelurahan || '-'}</Typography>
                    <Typography><strong>{t('backoffice.signupRequests.fields.postalCode')}:</strong> {viewDialog.request.postal_code || '-'}</Typography>
                  </CardContent>
                </Card>
              </Box>
              
              <Box sx={{ mb: 3 }}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>{t('backoffice.signupRequests.dialogs.statusReview')}</Typography>
                    <Box display="flex" gap={2} alignItems="center" mb={2}>
                      <Chip
                        label={getStatusLabel(viewDialog.request.status)}
                        color={getStatusColor(viewDialog.request.status)}
                      />
                      {viewDialog.request.epicor_sync_status && (
                        <Chip
                          label={`Epicor: ${viewDialog.request.epicor_sync_status}`}
                          color={viewDialog.request.epicor_sync_status === 'synced' ? 'success' : 'default'}
                          size="small"
                        />
                      )}
                    </Box>
                    <Typography><strong>{t('backoffice.signupRequests.fields.createdAt')}:</strong> {formatDate(viewDialog.request.submitted_at)}</Typography>
                    {viewDialog.request.reviewed_at && (
                      <Typography><strong>{t('backoffice.signupRequests.fields.reviewedAt')}:</strong> {formatDate(viewDialog.request.reviewed_at)}</Typography>
                    )}
                    {viewDialog.request.admin_notes && (
                      <Typography><strong>{t('backoffice.signupRequests.fields.adminNotes')}:</strong> {viewDialog.request.admin_notes}</Typography>
                    )}
                    {viewDialog.request.rejection_reason && (
                      <Typography><strong>{t('backoffice.signupRequests.fields.rejectionReason')}:</strong> {viewDialog.request.rejection_reason}</Typography>
                    )}
                    {viewDialog.request.epicor_customer_id && (
                      <Typography><strong>{t('backoffice.signupRequests.fields.epicorCustomerId')}:</strong> {viewDialog.request.epicor_customer_id}</Typography>
                    )}
                  </CardContent>
                </Card>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialog({ open: false, request: null })}>{t('backoffice.signupRequests.dialogs.close')}</Button>
          <Button 
            onClick={() => {
              if (viewDialog.request) {
                setViewDialog({ open: false, request: null });
                openEditDialog(viewDialog.request);
              }
            }}
            variant="contained"
            sx={{
              backgroundColor: MOWILEX_COLORS.primary,
              color: MOWILEX_COLORS.white,
              '&:hover': {
                backgroundColor: MOWILEX_COLORS.primaryDark
              }
            }}
          >
            Edit
          </Button>
        </DialogActions>
      </Dialog>

      {/* Epicor Verification Dialog */}
      <Dialog open={epicorDialog.open} onClose={() => setEpicorDialog({ open: false, request: null, result: null })} maxWidth="md" fullWidth>
        <DialogTitle>{t('backoffice.signupRequests.dialogs.epicorResultTitle')}</DialogTitle>
        <DialogContent>
          {epicorDialog.result && (
            <Box>
              <Typography variant="h6" gutterBottom>{t('backoffice.signupRequests.dialogs.verificationSummary')}</Typography>
              <Typography><strong>{t('backoffice.signupRequests.dialogs.status')}:</strong> {epicorDialog.result.overall_status}</Typography>
              <Typography><strong>{t('backoffice.signupRequests.dialogs.message')}:</strong> {epicorDialog.result.message}</Typography>
              
              <Divider sx={{ my: 2 }} />
              
              <Typography variant="h6" gutterBottom>{t('backoffice.signupRequests.dialogs.verificationDetails')}</Typography>
              {Object.entries(epicorDialog.result.verification_results || {}).map(([key, result]: [string, any]) => (
                <Card key={key} sx={{ mb: 2 }}>
                  <CardContent>
                    <Typography variant="subtitle1" fontWeight="bold">{key.replace('_', ' ').toUpperCase()}</Typography>
                    <Typography><strong>{t('backoffice.signupRequests.dialogs.status')}:</strong> {result.status}</Typography>
                    {result.message && <Typography><strong>{t('backoffice.signupRequests.dialogs.message')}:</strong> {result.message}</Typography>}
                    {result.epicor_data && (
                      <Box>
                        <Typography><strong>{t('backoffice.signupRequests.dialogs.epicorData')}:</strong></Typography>
                        <Typography variant="body2">
                          Company: {result.epicor_data.company_name}<br/>
                          Contact: {result.epicor_data.contact_person}<br/>
                          Email: {result.epicor_data.email}
                        </Typography>
                      </Box>
                    )}
                    {result.matches && (
                      <Box>
                        <Typography><strong>{t('backoffice.signupRequests.dialogs.dataMatches')}:</strong></Typography>
                        {Object.entries(result.matches).map(([field, matches]) => (
                          <Typography key={field} variant="body2">
                            {field}: {matches ? t('backoffice.signupRequests.dialogs.match') : t('backoffice.signupRequests.dialogs.noMatch')}
                          </Typography>
                        ))}
                      </Box>
                    )}
                  </CardContent>
                </Card>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEpicorDialog({ open: false, request: null, result: null })}>{t('backoffice.signupRequests.dialogs.close')}</Button>
        </DialogActions>
      </Dialog>

      {/* Approve Dialog */}
      <Dialog open={approveDialog.open} onClose={() => setApproveDialog({ open: false, request: null })} maxWidth="sm" fullWidth>
        <DialogTitle>{t('backoffice.signupRequests.approval.approveTitle')}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <TextField
              fullWidth
              label={t('backoffice.signupRequests.approval.adminNotes')}
              multiline
              rows={3}
              value={approvalForm.admin_notes}
              onChange={(e) => setApprovalForm(prev => ({ ...prev, admin_notes: e.target.value }))}
              margin="normal"
            />
            
            <FormControl component="fieldset" sx={{ mt: 2 }}>
              <FormLabel component="legend">{t('backoffice.signupRequests.approval.sendMagicLink')}</FormLabel>
              <RadioGroup
                value={approvalForm.send_magic_link}
                onChange={(e) => setApprovalForm(prev => ({ ...prev, send_magic_link: e.target.value === 'true' }))}
              >
                <FormControlLabel value={true} control={<Radio />} label={t('backoffice.signupRequests.approval.sendMagicLinkYes')} />
                <FormControlLabel value={false} control={<Radio />} label={t('backoffice.signupRequests.approval.sendMagicLinkNo')} />
              </RadioGroup>
            </FormControl>

            {approvalForm.send_magic_link && (
              <>
                <FormControl component="fieldset" sx={{ mt: 2 }}>
                  <FormLabel component="legend">{t('backoffice.signupRequests.approval.magicLinkDelivery')}</FormLabel>
                  <RadioGroup
                    value={approvalForm.delivery_method}
                    onChange={(e) => setApprovalForm(prev => ({ ...prev, delivery_method: e.target.value as any }))}
                  >
                    <FormControlLabel value="email" control={<Radio />} label={<Box display="flex" alignItems="center"><Email sx={{ mr: 1 }} />{t('backoffice.signupRequests.approval.email')}</Box>} />
                    <FormControlLabel value="whatsapp" control={<Radio />} label={<Box display="flex" alignItems="center"><WhatsApp sx={{ mr: 1 }} />{t('backoffice.signupRequests.approval.whatsapp')}</Box>} />
                    <FormControlLabel value="manual" control={<Radio />} label={<Box display="flex" alignItems="center"><ContentCopy sx={{ mr: 1 }} />{t('backoffice.signupRequests.approval.manual')}</Box>} />
                  </RadioGroup>
                </FormControl>

                <TextField
                  fullWidth
                  label={t('backoffice.signupRequests.approval.customMessage')}
                  multiline
                  rows={2}
                  value={approvalForm.custom_message}
                  onChange={(e) => setApprovalForm(prev => ({ ...prev, custom_message: e.target.value }))}
                  margin="normal"
                  helperText={t('backoffice.signupRequests.approval.additionalMessage')}
                />
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApproveDialog({ open: false, request: null })}>{t('common.cancel')}</Button>
          <Button 
            onClick={handleApprove} 
            variant="contained" 
            disabled={approveLoading}
            startIcon={approveLoading ? <CircularProgress size={16} /> : <CheckCircle />}
            sx={{
              backgroundColor: MOWILEX_COLORS.success,
              color: MOWILEX_COLORS.white,
              '&:hover': {
                backgroundColor: `${MOWILEX_COLORS.success}dd`
              }
            }}
          >
            {approveLoading ? t('common.loading') : t('common.approve')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={rejectDialog.open} onClose={() => setRejectDialog({ open: false, request: null })} maxWidth="sm" fullWidth>
        <DialogTitle>{t('backoffice.signupRequests.dialogs.rejectTitle')}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label={t('backoffice.signupRequests.approval.rejectReason')}
            multiline
            rows={4}
            value={rejectionReason}
            onChange={(e) => setRejectionReason(e.target.value)}
            margin="normal"
            required
            helperText={t('backoffice.signupRequests.approval.rejectConfirm')}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectDialog({ open: false, request: null })}>{t('common.cancel')}</Button>
          <Button 
            onClick={handleReject} 
            variant="contained" 
            disabled={rejectLoading || !rejectionReason.trim()}
            startIcon={rejectLoading ? <CircularProgress size={16} /> : <Cancel />}
            sx={{
              backgroundColor: MOWILEX_COLORS.error,
              color: MOWILEX_COLORS.white,
              '&:hover': {
                backgroundColor: `${MOWILEX_COLORS.error}dd`
              }
            }}
          >
            {rejectLoading ? t('common.loading') : t('common.reject')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Magic Link Dialog */}
      <Dialog open={magicLinkDialog.open} onClose={() => setMagicLinkDialog({ open: false, request: null })} maxWidth="sm" fullWidth>
        <DialogTitle>{t('backoffice.signupRequests.dialogs.magicLinkTitle')}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <FormControl component="fieldset">
              <FormLabel component="legend">{t('backoffice.signupRequests.approval.magicLinkDelivery')}</FormLabel>
              <RadioGroup
                value={magicLinkForm.delivery_method}
                onChange={(e) => setMagicLinkForm(prev => ({ ...prev, delivery_method: e.target.value as any }))}
              >
                <FormControlLabel value="email" control={<Radio />} label={<Box display="flex" alignItems="center"><Email sx={{ mr: 1 }} />{t('backoffice.signupRequests.approval.email')}</Box>} />
                <FormControlLabel value="whatsapp" control={<Radio />} label={<Box display="flex" alignItems="center"><WhatsApp sx={{ mr: 1 }} />{t('backoffice.signupRequests.approval.whatsapp')}</Box>} />
                <FormControlLabel value="manual" control={<Radio />} label={<Box display="flex" alignItems="center"><ContentCopy sx={{ mr: 1 }} />{t('backoffice.signupRequests.approval.manual')}</Box>} />
              </RadioGroup>
            </FormControl>

            <FormControl component="fieldset" sx={{ mt: 2 }}>
              <FormLabel component="legend">Token Options</FormLabel>
              <RadioGroup
                value={magicLinkForm.regenerate_token}
                onChange={(e) => setMagicLinkForm(prev => ({ ...prev, regenerate_token: e.target.value === 'true' }))}
              >
                <FormControlLabel value={false} control={<Radio />} label="Use existing token" />
                <FormControlLabel value={true} control={<Radio />} label="Generate new token" />
              </RadioGroup>
            </FormControl>

            <TextField
              fullWidth
              label={t('backoffice.signupRequests.approval.customMessage')}
              multiline
              rows={3}
              value={magicLinkForm.custom_message}
              onChange={(e) => setMagicLinkForm(prev => ({ ...prev, custom_message: e.target.value }))}
              margin="normal"
              helperText={t('backoffice.signupRequests.approval.additionalMessage')}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMagicLinkDialog({ open: false, request: null })}>{t('common.cancel')}</Button>
          <Button 
            onClick={handleSendMagicLink} 
            variant="contained" 
            disabled={magicLinkLoading}
            startIcon={magicLinkLoading ? <CircularProgress size={16} /> : <Link />}
            sx={{
              backgroundColor: MOWILEX_COLORS.primary,
              color: MOWILEX_COLORS.white,
              '&:hover': {
                backgroundColor: MOWILEX_COLORS.primaryDark
              }
            }}
          >
            {magicLinkLoading ? t('common.loading') : t('backoffice.signupRequests.approval.sendMagicLink')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editDialog.open} onClose={() => setEditDialog({ open: false, request: null })} maxWidth="md" fullWidth>
        <DialogTitle>{t('backoffice.signupRequests.dialogs.editTitle')}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            {/* Personal Information */}
            <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>{t('backoffice.signupRequests.dialogs.personalInfo')}</Typography>
            <TextField
              fullWidth
              label={t('backoffice.signupRequests.fields.fullName')}
              value={editForm.full_name}
              onChange={(e) => setEditForm(prev => ({ ...prev, full_name: e.target.value }))}
              margin="normal"
            />
            <TextField
              fullWidth
              label={t('backoffice.signupRequests.fields.phone')}
              value={editForm.phone}
              onChange={(e) => setEditForm(prev => ({ ...prev, phone: e.target.value }))}
              margin="normal"
            />

            {/* Company Information */}
            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>{t('backoffice.signupRequests.dialogs.companyInfo')}</Typography>
            <TextField
              fullWidth
              label={t('backoffice.signupRequests.fields.companyName')}
              value={editForm.company_name}
              onChange={(e) => setEditForm(prev => ({ ...prev, company_name: e.target.value }))}
              margin="normal"
            />
            <TextField
              fullWidth
              label={t('backoffice.signupRequests.fields.contactPerson')}
              value={editForm.contact_person}
              onChange={(e) => setEditForm(prev => ({ ...prev, contact_person: e.target.value }))}
              margin="normal"
            />
            <TextField
              fullWidth
              label={t('backoffice.signupRequests.fields.mowilexCustomerId')}
              value={editForm.mowilex_customer_id}
              onChange={(e) => setEditForm(prev => ({ ...prev, mowilex_customer_id: e.target.value }))}
              margin="normal"
            />
            <TextField
              fullWidth
              label={t('backoffice.signupRequests.fields.territory')}
              value={editForm.territory}
              onChange={(e) => setEditForm(prev => ({ ...prev, territory: e.target.value }))}
              margin="normal"
            />

            {/* Address Information */}
            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>{t('backoffice.signupRequests.dialogs.address')}</Typography>
            <TextField
              fullWidth
              label={t('backoffice.signupRequests.fields.address1')}
              value={editForm.address1}
              onChange={(e) => setEditForm(prev => ({ ...prev, address1: e.target.value }))}
              margin="normal"
            />
            <TextField
              fullWidth
              label={t('backoffice.signupRequests.fields.address2')}
              value={editForm.address2}
              onChange={(e) => setEditForm(prev => ({ ...prev, address2: e.target.value }))}
              margin="normal"
            />
            <TextField
              fullWidth
              label={t('backoffice.signupRequests.fields.city')}
              value={editForm.city}
              onChange={(e) => setEditForm(prev => ({ ...prev, city: e.target.value }))}
              margin="normal"
            />
            <TextField
              fullWidth
              label={t('backoffice.signupRequests.fields.province')}
              value={editForm.province}
              onChange={(e) => setEditForm(prev => ({ ...prev, province: e.target.value }))}
              margin="normal"
            />
            <TextField
              fullWidth
              label={t('backoffice.signupRequests.fields.district')}
              value={editForm.kecamatan}
              onChange={(e) => setEditForm(prev => ({ ...prev, kecamatan: e.target.value }))}
              margin="normal"
            />
            <TextField
              fullWidth
              label={t('backoffice.signupRequests.fields.village')}
              value={editForm.kelurahan}
              onChange={(e) => setEditForm(prev => ({ ...prev, kelurahan: e.target.value }))}
              margin="normal"
            />
            <TextField
              fullWidth
              label={t('backoffice.signupRequests.fields.postalCode')}
              value={editForm.postal_code}
              onChange={(e) => setEditForm(prev => ({ ...prev, postal_code: e.target.value }))}
              margin="normal"
            />
            
            {/* Epicor Information */}
            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>{t('backoffice.signupRequests.dialogs.statusReview')}</Typography>
            <TextField
              fullWidth
              label={t('backoffice.signupRequests.fields.epicorCustomerId')}
              value={editForm.epicor_customer_id}
              onChange={(e) => setEditForm(prev => ({ ...prev, epicor_customer_id: e.target.value }))}
              margin="normal"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialog({ open: false, request: null })}>{t('common.cancel')}</Button>
          <Button 
            onClick={handleEdit}
            variant="contained"
            disabled={editLoading}
            startIcon={editLoading ? <CircularProgress size={16} /> : undefined}
            sx={{
              backgroundColor: MOWILEX_COLORS.success,
              color: MOWILEX_COLORS.white,
              '&:hover': {
                backgroundColor: `${MOWILEX_COLORS.success}dd`
              }
            }}
          >
            {editLoading ? t('common.loading') : t('backoffice.signupRequests.dialogs.save')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Verify Confirmation Dialog */}
      <Dialog open={verifyConfirmDialog.open} onClose={() => setVerifyConfirmDialog({ open: false, request: null })} maxWidth="sm" fullWidth>
        <DialogTitle>{t('backoffice.signupRequests.dialogs.verifyConfirmTitle')}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <Typography variant="body1" paragraph>
              {t('backoffice.signupRequests.verification.confirmMessage')}
            </Typography>
            
            {verifyConfirmDialog.request && (
              <Box>
                <Typography variant="body2" sx={{ mt: 2, mb: 1 }}>
                  <strong>{t('backoffice.signupRequests.verification.customerIdInfo')}</strong>
                </Typography>
                <Typography variant="body1" color="primary" sx={{ mb: 2 }}>
                  {verifyConfirmDialog.request.mowilex_customer_id || t('backoffice.signupRequests.fields.none')}
                </Typography>
                
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>{t('backoffice.signupRequests.verification.emailInfo')}</strong>
                </Typography>
                <Typography variant="body1" color="primary">
                  {verifyConfirmDialog.request.email}
                </Typography>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setVerifyConfirmDialog({ open: false, request: null })}>
            {t('common.cancel')}
          </Button>
          <Button 
            variant="contained"
            onClick={() => verifyConfirmDialog.request && handleVerifyEpicor(verifyConfirmDialog.request)}
            sx={{
              backgroundColor: MOWILEX_COLORS.primary,
              '&:hover': {
                backgroundColor: MOWILEX_COLORS.primaryDark
              }
            }}
          >
            {t('backoffice.signupRequests.dialogs.verify')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Magic Link Result Dialog */}
      <Dialog open={magicLinkResult.open} onClose={() => setMagicLinkResult({open: false, url: '', email: ''})} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center">
            <Link sx={{ mr: 1 }} />
            {t('backoffice.signupRequests.magicLink.title')}
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <Typography variant="body1" sx={{ mb: 2 }}>
              {t('backoffice.signupRequests.magicLink.description')}
            </Typography>
            
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>{t('backoffice.signupRequests.magicLink.customerEmail')}:</strong> {magicLinkResult.email}
            </Typography>
            
            <Box sx={{ p: 2, bgcolor: 'grey.100', borderRadius: 1, border: '1px solid', borderColor: 'grey.300' }}>
              <Typography variant="body2" sx={{ wordBreak: 'break-all', mb: 1 }}>
                {magicLinkResult.url}
              </Typography>
              <Button
                size="small"
                startIcon={<ContentCopy />}
                onClick={() => {
                  navigator.clipboard.writeText(magicLinkResult.url);
                  setSuccess(t('backoffice.signupRequests.magicLink.copySuccess'));
                }}
                variant="outlined"
              >
                {t('backoffice.signupRequests.magicLink.copyButton')}
              </Button>
            </Box>
            
            <Alert severity="info" sx={{ mt: 2 }}>
              {t('backoffice.signupRequests.magicLink.securityNote')}
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMagicLinkResult({open: false, url: '', email: ''})} variant="contained">
            {t('common.close')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SignupRequestsPage;